/**
 * Was ein Credit im Alltag kauft, damit sich abschätzen lässt, wie weit ein
 * Plan reicht. KEINE ERFUNDENEN ZAHLEN: jede steht im Code oder ist dort
 * gemessen, und `preise.test.ts` liest die Quellen nach.
 *
 *   Vergleich, Standard           73   12 Personas (Mitte der 10 bis 14 aus start_research,
 *                                      research.ts) mit 4 Jobs, Persona × Job: min(MAX_ZELLEN 48,
 *                                      12 × 4) = 48 Sim-Runs (matrix.ts, matrixPreis).
 *                                      preis_run.ts: ceil(48 × 1,5) + 1 für die erzeugte
 *                                      Vergleichsfassung, bei kurzen Texten. Seit 05.10.2026
 *                                      (vorher „Vergleich mit 6 Personas", 10).
 *   Vergleich 1:1                 19   dieselben 12 Personas mit matrix: false, je Persona nur
 *                                      ihr eigener Job: ceil(12 × 1,5) + 1
 *   Seiten-Ausbau                267   Preisfolge bei zwölf Personas in supabase/functions/_shared/sim/BUILD.md,
 *                             bis 444   mit Verzweigung an der Hero. Der Ausbau bleibt 1:1.
 *   Kernaussagen-Abgleich        3–7   besetzt.ts: GRUNDPREIS 2 + 1 je Wettbewerbstext, 1 bis MAX_WETTBEWERBER 5
 *   Vorauswahl                   3–5   vorauswahl.ts: je Person der Jury, JURY_STANDARD 3 bis JURY_MAX 5
 *   Challenger-Fassungen        4–18   schreiben.ts: GRUNDPREIS 3 + je Fassung 1 je angefangene
 *                                      ZEICHEN_JE_CREDIT 4.000 Zeichen; eine kurze Fassung 4,
 *                                      MAX_ANZAHL 5 Fassungen einer Seite mit MAX_TEXT_CHARS 12.000 Zeichen 18.
 *                                      Drei Fassungen einer Headline: 6.
 *   Design-Check                bis 12   design.ts: DESIGN_CREDITS_JE_PERSONA 1,5 je Persona,
 *                                      höchstens DESIGN_MAX_PERSONAS 8: ceil(8 × 1,5)
 */

import { locale, t } from './sprache';

/** Ein typisches Set nach dem Standard seit 05.10.2026: 12 Personas, 4 Jobs, 48 Sim-Runs. */
export const STANDARD = { personas: 12, jobs: 4, simRuns: 48 } as const;
/** Vergleich mit Persona × Job, Vergleichsfassung erzeugt, kurzer Text. */
export const VERGLEICH_STANDARD = 73;
/** Derselbe Vergleich 1:1 (matrix: false), Vergleichsfassung erzeugt, kurzer Text. */
export const VERGLEICH_1ZU1 = 19;
/** Eine neue Zielgruppe beim Speichern (sets.ts, EINRICHTUNG_CREDITS; seit 30.09.2026). */
export const EINRICHTUNG = 15;
/** Seiten-Ausbau bei zwölf Personas (BUILD.md, „Preisfolge“), mit Verzweigung an der Hero bis `bis`. */
export const AUSBAU = { von: 267, bis: 444 } as const;
export const ABGLEICH = { von: 3, bis: 7 } as const;
export const VORAUSWAHL = { von: 3, bis: 5 } as const;
export const CHALLENGER = { von: 4, bis: 18, dreiKurz: 6 } as const;
/** Design-Check (design.ts): DESIGN_CREDITS_JE_PERSONA je Persona, höchstens DESIGN_MAX_PERSONAS; seit 06.10.2026. */
export const DESIGN = { jePersona: 1.5, maxPersonas: 8, maxCredits: 12 } as const;

/** Wie viele Vergleiche zweier Texte, wie sie ohne Angabe laufen (VERGLEICH_STANDARD), eine Menge Credits trägt. Abgerundet. */
export function reichweite(credits: number): number {
  return Math.floor(Math.max(0, Math.floor(credits)) / VERGLEICH_STANDARD);
}

const zahl = (n: number) => new Intl.NumberFormat(locale()).format(n);

/**
 * Eine Zeile unter Plan oder Paket: „reicht für rund 20 Vergleiche zweier
 * Texte". Bis zum 19.10.2026 stand dort „≈ 20 Vergleiche mit 12 Personas × Job
 * (78 als 1:1) oder 3 bis 5 Seiten-Ausbauten"; ein Testnutzer verstand an der
 * Kaufentscheidung weder „Persona × Job" noch „1:1" (Prüfung der Bedienung,
 * Befund 5). Die Tabelle „Was ein Credit kauft" nennt beides weiter mit Preis.
 */
export function reichweiteSatz(credits: number): string {
  const n = reichweite(credits);
  if (n === 0) return t('reicht noch nicht für einen Vergleich zweier Texte', 'not yet enough for a comparison of two texts');
  if (n === 1) return t('reicht für einen Vergleich zweier Texte', 'enough for one comparison of two texts');
  return t(`reicht für rund ${zahl(n)} Vergleiche zweier Texte`, `enough for about ${zahl(n)} comparisons of two texts`);
}

/**
 * Endpreis mit Umsatzsteuer (#270, seit 16.10.2026). Gegenüber Verbrauchern
 * muss der Endpreis stehen (Preisangabenverordnung); in der Datenbank stehen
 * Nettopreise (`preis_netto_cent`), gerechnet wird wie bei Mollie
 * (`brutto` in steuer.ts: kaufmännisch auf den Cent gerundet).
 *
 * `satz` kommt aus den Rechnungsdaten des Kontos (`status.steuer.satz`);
 * ohne Rechnungsdaten gilt der deutsche Satz, weil der Kauf für Verbraucher
 * außerhalb Deutschlands ohnehin gesperrt ist (OSS, steuer.ts). Mit 0 %
 * (Reverse Charge, Drittland) steht der Nettopreis ohne Steuer.
 */
export const SATZ_DE = '19.00';

export function bruttoCent(nettoCent: number, satz: string = SATZ_DE): number {
  return Math.round(nettoCent * (1 + Number(satz) / 100));
}

export const euro = (cent: number) =>
  new Intl.NumberFormat(locale(), { style: 'currency', currency: 'EUR' }).format(cent / 100);

const prozent = (satz: string) => new Intl.NumberFormat(locale(), { maximumFractionDigits: 2 }).format(Number(satz));

export interface PreisAnzeige {
  /** Der Preis, der groß steht: brutto, bei 0 % netto. */
  haupt: string;
  /** Darunter: Steuer und Nettopreis für Unternehmen. */
  zusatz: string;
  /** Der Betrag in Cent, der abgebucht wird. */
  cent: number;
}

export function preisAnzeige(nettoCent: number, satz: string = SATZ_DE): PreisAnzeige {
  if (!(Number(satz) > 0)) return { haupt: euro(nettoCent), zusatz: t('ohne Umsatzsteuer', 'no VAT'), cent: nettoCent };
  const cent = bruttoCent(nettoCent, satz);
  return {
    haupt: euro(cent),
    zusatz: t(`inkl. ${prozent(satz)} % USt., ${euro(nettoCent)} netto`, `incl. ${prozent(satz)}% VAT, ${euro(nettoCent)} net`),
    cent,
  };
}

/** Cent je Credit aus einem Betrag in Cent, eine Nachkommastelle. */
export function centJeCreditAus(cent: number, credits: number): string | null {
  if (!(credits > 0) || !(cent > 0)) return null;
  return new Intl.NumberFormat(locale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(cent / credits);
}

/** Preis je Credit in Cent, eine Stelle, aus einem Preisschild wie „49 € / Monat" oder „119 €". */
export function centJeCredit(preisLabel: string, credits: number): string | null {
  const m = preisLabel.replace(/\./g, '').match(/(\d+(?:,\d+)?)\s*€/);
  if (!m || !(credits > 0)) return null;
  const euro = Number(m[1]!.replace(',', '.'));
  return new Intl.NumberFormat(locale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format((euro / credits) * 100);
}

/**
 * Was ein Plan außer Credits bringt (Entscheidung Robert, 09.10.2026): wie
 * viele eigene Zielgruppen und ob Berichte nur für eingeladene Adressen gehen.
 * Die Werte kommen aus sim_plans über die billing-Function, ohne Plan aus
 * OHNE_PLAN in plan_merkmale.ts. Fehlt ein Wert (Function von davor), steht
 * keine Zeile da statt einer geratenen.
 */
export function zielgruppenZeile(max: number | null | undefined): string | null {
  if (max === undefined) return null;
  if (max === null) return t('Eigene Zielgruppen unbegrenzt', 'Unlimited own audiences');
  return max === 1 ? t('1 eigene Zielgruppe', '1 own audience') : t(`${zahl(max)} eigene Zielgruppen`, `${zahl(max)} own audiences`);
}

export function leserZeile(leserliste: boolean | undefined): string | null {
  if (leserliste === undefined) return null;
  return leserliste ? t('Berichte nur für eingeladene Adressen', 'Reports for invited addresses only') : t('Berichte mit offenem Link', 'Reports with an open link');
}

/** Nur eine Zeile, wenn der Plan es kann: „nein“ steht schon bei den kleineren Plänen nicht. */
export function gespraecheZeile(eigene: boolean | undefined): string | null {
  return eigene ? t('Eigene Interviews und Tickets hochladen', 'Upload your own interviews and tickets') : null;
}

/**
 * Was der Knopf an einer Plan-Karte tut.
 *   verwalten  der eigene Plan: Kündigen in der App (Mollie, seit 07.10.2026)
 *   wechseln   es läuft ein Abo, dies ist ein anderer Plan: Wechsel ab der
 *              nächsten Abbuchung. Der Checkout lehnt ein zweites Abo ab (409).
 *   waehlen    kein Abo, Plan buchbar: Mollie-Zahlung für den ersten Monat
 *   bald       nicht kaufbar (nicht aktiv, oder Testmodus und kein Testkonto)
 * Fehlt `buchbar` (billing-Function von vor dem 27.09.2026), war der Plan nur
 * in der Liste, weil er aktiv war.
 */
export type PlanAktion = 'verwalten' | 'wechseln' | 'waehlen' | 'bald';

export function planAktion(
  p: { plan: string; buchbar?: boolean },
  s: { plan: string; subscription: { status: string } | null },
): PlanAktion {
  const abo = !!s.subscription && s.subscription.status !== 'canceled';
  if (abo && p.plan === s.plan) return 'verwalten';
  if (p.buchbar === false) return 'bald';
  return abo ? 'wechseln' : 'waehlen';
}

/**
 * Der Teil des Guthabens außerhalb des Monatskontingents, als Satz. Gekaufte
 * Pakete verfielen vom 27.09. bis 06.10.2026 nach 12 Monaten und nennen dann ihr
 * frühestes Verfallsdatum, seitdem verfallen sie nicht; was übrig bleibt, sind Gutschriften ohne Verfall
 * (Handbuchungen). Eine billing-Function von davor kennt `pack_remaining`
 * nicht: dann ist alles Gutschrift, denn Pakete wurden vorher nie verkauft.
 */
export function guthabenRest(
  g: { pack_credits?: number; pack_remaining?: number; pack_expires_at?: string | null },
  zahl: (n: number) => string,
  tag: (iso: string | null | undefined) => string,
): string | null {
  const pakete = g.pack_remaining ?? 0;
  const gutschrift = Math.max(0, (g.pack_credits ?? 0) - pakete);
  const teile = [
    // Seit 06.10.2026 verfallen gekaufte Pakete nicht; nur ältere nennen ein Datum.
    pakete > 0 ? (g.pack_expires_at
      ? t(`${zahl(pakete)} aus Paketen, das nächste verfällt am ${tag(g.pack_expires_at)}`, `${zahl(pakete)} from packs, the next expires on ${tag(g.pack_expires_at)}`)
      : t(`${zahl(pakete)} aus Paketen, ohne Verfall`, `${zahl(pakete)} from packs, no expiry`)) : null,
    gutschrift > 0 ? t(`${zahl(gutschrift)} als Gutschrift ohne Verfall`, `${zahl(gutschrift)} as a credit grant, no expiry`) : null,
  ].filter(Boolean);
  return teile.length ? `${teile.join('. ')}.` : null;
}
